import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { APP_VERSION, fetchVersionInfo, isOutdated, type VersionInfo } from '../lib/appVersion';

interface VersionContextValue {
  /** Version dieses Bundles (fest einkompiliert). */
  running: string;
  /** Zuletzt vom Server gemeldeter Stand — null, solange nichts geladen wurde. */
  info: VersionInfo | null;
  /** Server hat eine andere Version als dieses Bundle → Reload nötig. */
  updateAvailable: boolean;
  /** Lädt den Serverstand sofort neu (z.B. beim Öffnen des Verlaufs-Modals). */
  refresh: () => Promise<void>;
}

const VersionContext = createContext<VersionContextValue>({
  running: APP_VERSION,
  info: null,
  updateAvailable: false,
  refresh: async () => {},
});

/** Poll-Intervall für den Versionscheck. */
const POLL_MS = 60_000;

export function VersionProvider({ children }: { children: React.ReactNode }) {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchVersionInfo();
      if (!cancelledRef.current) setInfo(next);
    } catch {
      // Netzwerkfehler ignorieren — der nächste Poll versucht es erneut. Ein
      // Ausfall der API darf keinen falschen Update-Hinweis erzeugen.
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MS);
    // Zusätzlich beim Zurückkehren in den Tab prüfen: nach einer Pause ist ein
    // Deploy der wahrscheinlichste Grund, dass die Oberfläche veraltet ist.
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelledRef.current = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  return (
    <VersionContext.Provider
      value={{
        running: APP_VERSION,
        info,
        updateAvailable: isOutdated(APP_VERSION, info?.version),
        refresh,
      }}
    >
      {children}
    </VersionContext.Provider>
  );
}

export const useVersion = () => useContext(VersionContext);
